import React from 'react';
import { useIntl } from 'react-intl';
import { CheckCircle2 as CheckCircle2Icon, AlertTriangle as AlertTriangleIcon, XCircle as XCircleIcon } from 'lucide-react';

/**
 * ConfidenceBadge - Inline badge showing confidence level
 * Pixel-perfect port from Polymet confidence-badge.tsx
 *
 * @param {Object} props
 * @param {'low' | 'medium' | 'high'} props.level - Confidence level
 * @param {number} [props.score] - Optional numeric score (0-100)
 * @param {boolean} [props.showIcon=true] - Whether to show the icon
 * @param {boolean} [props.showScore=false] - Whether to show the score percentage
 * @param {'sm' | 'md' | 'lg'} [props.size='md'] - Badge size
 * @param {string} [props.className] - Additional CSS classes
 */
const ConfidenceBadge = ({
  level,
  score,
  showIcon = true,
  showScore = false,
  size = 'md',
  className = '',
}) => {
  const intl = useIntl();

  // Using inline styles to ensure colors render correctly
  const config = {
    low: {
      label: intl.formatMessage({ id: 'succession.confidence_rating.badge.low_label' }),
      shortLabel: intl.formatMessage({ id: 'succession.confidence_rating.badge.low_short' }),
      icon: XCircleIcon,
      styles: {
        backgroundColor: '#FEE2E2', // red-100
        color: '#B91C1C', // red-700
        borderColor: '#FECACA', // red-200
      },
      iconColor: '#DC2626', // red-600
    },
    medium: {
      label: intl.formatMessage({ id: 'succession.confidence_rating.badge.medium_label' }),
      shortLabel: intl.formatMessage({ id: 'succession.confidence_rating.badge.medium_short' }),
      icon: AlertTriangleIcon,
      styles: {
        backgroundColor: '#FEF3C7', // amber-100
        color: '#B45309', // amber-700
        borderColor: '#FDE68A', // amber-200
      },
      iconColor: '#D97706', // amber-600
    },
    high: {
      label: intl.formatMessage({ id: 'succession.confidence_rating.badge.high_label' }),
      shortLabel: intl.formatMessage({ id: 'succession.confidence_rating.badge.high_short' }),
      icon: CheckCircle2Icon,
      styles: {
        backgroundColor: '#D1FAE5', // emerald-100
        color: '#047857', // emerald-700
        borderColor: '#A7F3D0', // emerald-200
      },
      iconColor: '#059669', // emerald-600
    },
  };

  const sizeConfig = {
    sm: {
      container: 'px-2 py-1 text-xs gap-1',
      icon: 'w-3 h-3',
    },
    md: {
      container: 'px-3 py-1.5 text-sm gap-1.5',
      icon: 'w-4 h-4',
    },
    lg: {
      container: 'px-4 py-2 text-base gap-2',
      icon: 'w-5 h-5',
    },
  };

  const levelConfig = config[level] || config.low;
  const sizeStyles = sizeConfig[size] || sizeConfig.md;
  const Icon = levelConfig.icon;

  return (
    <div
      className={`inline-flex items-center rounded-full border font-medium ${sizeStyles.container} ${className}`}
      style={{
        backgroundColor: levelConfig.styles.backgroundColor,
        color: levelConfig.styles.color,
        borderColor: levelConfig.styles.borderColor,
      }}
    >
      {showIcon && (
        <Icon
          className={sizeStyles.icon}
          style={{ color: levelConfig.iconColor }}
        />
      )}
      <span>{size === 'sm' ? levelConfig.shortLabel : levelConfig.label}</span>
      {showScore && score !== undefined && (
        <span className="ml-1 opacity-75">({score}%)</span>
      )}
    </div>
  );
};

export default ConfidenceBadge;
